// Cross-window remote control ("Regie/Output split").
//
// Why this exists: in Live/Resolume mode the output window hides every button (that's the whole
// point - a clean feed for the projector). But then how do you actually drive the show? The
// answer used everywhere in real VJ software (Resolume itself included) is to split the UI from
// the picture: one window shows the picture (captured by Syphon/OBS/NDI), a second window - kept
// on your own screen, never captured - shows the full button/slider UI and drives the first one
// remotely.
//
// This module is the wire between those two windows. Both are just the same page loaded twice
// (?role=control on your laptop screen, ?role=output fullscreen/captured); BroadcastChannel keeps
// their `state` objects in sync (same-browser, same-origin only - no server round-trip, ~0 delay).
//
// Two kinds of message:
//   - "state"  - a snapshot of every button/slider-controlled field, diffed and sent a few times
//                a second. Cheap enough to just poll-and-diff rather than hook every single call
//                site (toggleEffect, keybinds, sliders, MIDI, auto-mix all "just work").
//   - "action" - one-shot, non-stateful triggers (explosion / RB-burst) that don't live in
//                `state` at all, so they're sent as an explicit fire-and-forget event instead.

import { state } from "./state.js";

const CHANNEL_NAME = "starcode-sync";
const POLL_MS = 60; // ~16/s - imperceptible lag for held attractors, sliders, toggles

// Every field a button, slider, keybind or MIDI target can change. Kept as an explicit list
// (rather than reflecting over `state`) so transient/per-window bookkeeping fields (fps counters,
// audioReady, midiConnected, lastToggledEffect timestamps, ...) never leak between windows.
const SYNCED_FIELDS = [
  "gravityOn", "fireballOn", "sunOn", "ringOn", "rippleOn", "slowMotion", "explosionAudioOn", "morphOn",
  "negAttractorHeld", "posAttractorHeld",
  "wellsOn", "galaxySpiralOn", "galaxyDiscOn", "cosmicGalaxyOn", "galaxy04On", "ringNebulaOn",
  "swarmOn", "vortexOn",
  "blackHoleOn", "neutronStarOn",
  "liquidRippleOn", "inkBleedOn", "portalOn", "causticsOn", "chaosBoostOn", "distortionOn",
  "fractalOn", "patternOn", "redCircleOn", "shapeChangeOn", "streamsOn", "starSkyOn",
  "colorInvertOn", "scalePulseOn", "spiralHighOn", "bandExplodeOn", "shimmerOn",
  "complexOn", "permanentColor",
  "bioluminescenceOn", "bioSynapseOn", "bioSynapseIntensity", "spectrumColorOn", "chromaWaveOn", "beatFlashOn", "audioSizeOn",
  "beatPulseOn", "breathOn", "tunnelOn", "helixOn", "strobeFieldOn", "spectralCathedralOn", "pulseBloomOn", "gridPulseOn", "sphereOn",
  "spin360On", "cameraFollowOn", "autoCamOn", "autoCamSpeed", "shockwaveAudioOn", "muteLocalRender",
  "objDef1On", "objDef2On", "objDef3On", "electroOn", "fireflyRiverOn", "blackoutActive", "cameraFlightMode",
  "crystalOn", "hexMatrixOn", "fibonacciGridOn", "bioCellOn",
  "antimatterCollisionOn", "cryoLatticeOn", "hyperdriveWarpOn",
  "objMorphOn", "brandMorphOn",
  "wrapModeOn", "autoMixOn", "autoMixIntensity", "liveModeOn",
  "audioReactGlobal", "audioReactAttractors", "audioReactBasis", "audioReactEffects", "audioReactExtended",
  "outputPreset", "outputWidth", "outputHeight", "particleCount",
  "masterIntensity", "sizeBoost", "hueShift", "bloomAmount", "speedScale", "audioGain", "audioNoiseGate",
  "rotationSpeed", "rotationAudioReact", "bioIntensity", "bioSpeed", "colorWheelActive",
  "focusDistance", "dofBlur", "dofInvert", "dofAudioReact", "autofocusOn", "autoCamDistance",
  "sizeMultiplier",
];

let channel = null;
let applyingRemote = false;
let lastSent = {};

// particleSystem is only needed on the window that actually renders (the output), so one-shot
// actions arriving from a control window have something to act on.
export function initRemoteSync({ particleSystem, onAction, onCamera } = {}) {
  if (typeof BroadcastChannel === "undefined") return; // unsupported browser - single-window use still works fine

  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (e) => {
    const msg = e.data;
    if (!msg) return;
    if (msg.type === "state") {
      applyingRemote = true;
      Object.assign(state, msg.payload);
      if (msg.payload.particleCount && particleSystem) {
        particleSystem.setParticleCount(msg.payload.particleCount);
      }
      Object.assign(lastSent, msg.payload); // don't immediately echo it straight back
      applyingRemote = false;
      if (msg.payload.outputWidth || msg.payload.outputHeight || msg.payload.outputPreset) {
        window.dispatchEvent(new Event("resize"));
      }
    } else if (msg.type === "action") {
      if (particleSystem) {
        if (msg.action === "explosion") particleSystem.triggerExplosion(msg.value ?? 90);
        if (msg.action === "rbBurst") particleSystem.triggerRbBurst(msg.value ?? 90);
        if (msg.action === "shockwave") particleSystem.triggerShockwave(msg.value ?? 180);
        if (msg.action === "resetSwarm") particleSystem.resetSwarm();
      }
      onAction?.(msg.action, msg.value);
    } else if (msg.type === "camera") {
      onCamera?.(msg.payload);
    }
  };

  setInterval(() => {
    if (applyingRemote) return;
    const diff = {};
    let changed = false;
    for (const field of SYNCED_FIELDS) {
      const v = state[field];
      if (lastSent[field] !== v) {
        diff[field] = v;
        lastSent[field] = v;
        changed = true;
      }
    }
    if (changed) channel.postMessage({ type: "state", payload: diff });
  }, POLL_MS);
}

// For one-shot triggers (explosion/RB-burst) fired from a window with no particleSystem of its
// own (the control window) - the output window's onmessage handler above performs the trigger.
export function broadcastAction(action, value) {
  channel?.postMessage({ type: "action", action, value });
}

export function broadcastCameraState(payload) {
  channel?.postMessage({ type: "camera", payload });
}
